import React, { useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import { Upload, FileText } from 'lucide-react';
import { useTranslation } from '../../../hooks/useTranslation';
import { DropzoneRootProps, DropzoneInputProps } from 'react-dropzone';
import LoadingOverlay from './LoadingOverlay';

interface UploadSectionProps {
    getRootProps: <T extends DropzoneRootProps>(props?: T) => T;
    getInputProps: <T extends DropzoneInputProps>(props?: T) => T;
    isDragActive: boolean;
    file: File | null;
    setFile: (file: File | null) => void;
    fileType: 'xml' | 'pdf';
    uploadedFile?: string | null;
    handleFileChange?: (files: File[]) => void;
    loading: boolean;
    onFileChange?: (file: File | null) => void;
    previewFiles?: { file: File; previewUrl: string }[];
    processingStartTime?: number;
}

const UploadSection: React.FC<UploadSectionProps> = ({
    getRootProps,
    getInputProps,
    isDragActive,
    file,
    setFile,
    fileType,
    uploadedFile,
    handleFileChange,
    loading,
    onFileChange,
    previewFiles = [],
    processingStartTime,
}) => {
    const { t } = useTranslation();
    const [previewName, setPreviewName] = useState<string | null>(null);
    const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => {
        if (file) {
            setPreviewName(file.name);
        }
    }, [file]);

    // Manage loading overlay state for proper progress animation
    useEffect(() => {
        console.log('📱 UploadSection state change:', { loading, file: !!file, showLoadingOverlay, isProcessing });
        
        if (loading && file) {
            console.log('🟢 Starting processing - showing overlay');
            setShowLoadingOverlay(true);
            setIsProcessing(true);
        } else if (!loading && showLoadingOverlay) {
            console.log('🔴 Processing finished - starting rush animation');
            // Processing finished, allow rush-to-complete animation
            setIsProcessing(false);
            // Hide overlay after animation completes
            setTimeout(() => {
                console.log('⏰ Hiding overlay after animation');
                setShowLoadingOverlay(false);
            }, 800);
        }
    }, [loading, file, showLoadingOverlay]);

    const handleRemove = (e: React.MouseEvent) => {
        e.stopPropagation();
        setFile(null);
        setPreviewName(null);
        if (handleFileChange) {
            handleFileChange([]);
        }
        if (onFileChange) {
            onFileChange(null);
        }
    };

    const removePreview = (e: React.MouseEvent, fileToRemove: File) => {
        e.stopPropagation();
        e.preventDefault();
        e.nativeEvent.stopImmediatePropagation();
        if (handleFileChange) {
            const remaining = previewFiles.map(pf => pf.file).filter(f => f !== fileToRemove);
            handleFileChange(remaining);
        }
    };

    const renderFilePreview = () => {
        if (!file) return null;

        if (fileType === 'pdf' && uploadedFile) {
            return (
                <div className="flex flex-col items-center gap-4">
                    <div className="relative w-48">
                        <div className="h-64 bg-bg-tertiary rounded-lg overflow-hidden border border-border-primary">
                            <iframe
                                src={`${uploadedFile}#view=FitH`}
                                className="w-full h-full"
                                style={{
                                    transform: 'scale(1)',
                                    transformOrigin: 'top center',
                                    height: '150%',
                                    pointerEvents: 'none',
                                }}
                                title="PDF Preview"
                            />
                        </div>
                        <button
                            onClick={handleRemove}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-[var(--bg-tertiary)] hover:bg-[var(--bg-secondary)] rounded-full flex items-center justify-center text-[var(--text-primary)] transition-colors duration-150 z-10"
                            title="Remove file"
                        >
                            <FiX className="w-4 h-4" />
                        </button>
                    </div>
                    <div className="bg-hover-bg rounded-lg px-4 py-2 w-48">
                        <p className="text-brand-primary font-medium text-center text-sm truncate">
                            {file.name}
                        </p>
                        <p className="text-text-secondary text-xs text-center mt-1">
                            {(file.size / (1024 * 1024)).toFixed(2)} MB
                        </p>
                    </div>
                </div>
            );
        } else {
            return (
                <div className="flex flex-col items-center gap-4">
                    <div className="relative flex flex-col items-center">
                        <p className="text-text-primary font-medium text-sm mb-3">
                            {previewName}
                        </p>
                        <div className="w-20 h-24 relative">
                            <svg
                                fill="currentColor"
                                viewBox="0 0 58 58"
                                className="w-full h-full text-brand-primary"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <g>
                                    <path d="M51.5,39V13.978c0-0.766-0.092-1.333-0.55-1.792L39.313,0.55C38.964,0.201,38.48,0,37.985,0H8.963C7.777,0,6.5,0.916,6.5,2.926V39H51.5z M37.5,3.391c0-0.458,0.553-0.687,0.877-0.363l10.095,10.095C48.796,13.447,48.567,14,48.109,14H37.5V3.391z M33.793,18.707c-0.391-0.391-0.391-1.023,0-1.414s1.023-0.391,1.414,0l6,6c0.391,0.391,0.391,1.023,0,1.414l-6,6C35.012,30.902,34.756,31,34.5,31s-0.512-0.098-0.707-0.293c-0.391-0.391-0.391-1.023,0-1.414L39.086,24L33.793,18.707z M24.557,31.667l6-17c0.185-0.521,0.753-0.795,1.276-0.61c0.521,0.184,0.794,0.755,0.61,1.276l-6,17C26.298,32.744,25.912,33,25.5,33c-0.11,0-0.223-0.019-0.333-0.058C24.646,32.759,24.373,32.188,24.557,31.667z M15.793,23.293l6-6c0.391-0.391,1.023-0.391,1.414,0s0.391,1.023,0,1.414L17.914,24l5.293,5.293c0.391,0.391,0.391,1.023,0,1.414C23.012,30.902,22.756,31,22.5,31s-0.512-0.098-0.707-0.293l-6-6C15.402,24.316,15.402,23.684,15.793,23.293z"/>
                                    <path d="M6.5,41v15c0,1.009,1.22,2,2.463,2h40.074c1.243,0,2.463-0.991,2.463-2V41H6.5z M22.936,54h-1.9l-1.6-3.801h-0.137L17.576,54h-1.9l2.557-4.895l-2.721-5.182h1.873l1.777,4.102h0.137l1.928-4.102H23.1l-2.721,5.182L22.936,54z M34.666,54h-1.668v-6.932l-2.256,5.605h-1.449l-2.27-5.605V54h-1.668V43.924h1.668l2.994,6.891l2.98-6.891h1.668V54z M43.498,54h-6.303V43.924h1.668v8.832h4.635V54z"/>
                                </g>
                            </svg>
                        </div>
                        <p className="text-text-secondary text-xs mt-2">
                            {(file.size / (1024 * 1024)).toFixed(2)} MB
                        </p>
                        <button
                            onClick={handleRemove}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-[var(--bg-tertiary)] hover:bg-[var(--bg-secondary)] rounded-full flex items-center justify-center text-[var(--text-primary)] transition-colors duration-150 z-10"
                            title="Remove file"
                        >
                            <FiX className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            );
        }
    };

    // Disable dropzone during processing
    const dropzoneProps = loading ? {} : getRootProps();
    
    return (
        <div {...dropzoneProps} className={`
                relative w-full min-h-[400px] border-2 border-dashed rounded-2xl 
                flex flex-col items-center justify-center transition-all duration-300 p-8 overflow-hidden group
                ${loading 
                    ? 'cursor-not-allowed border-[var(--border-primary)] opacity-75'
                    : isDragActive
                        ? 'border-[var(--brand-primary)] bg-[var(--brand-primary)]/5 scale-105 shadow-xl cursor-pointer'
                        : 'border-[var(--border-primary)] hover:border-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/5 hover:shadow-lg cursor-pointer'
                }`}>
            {!loading && <input {...getInputProps()} />}
            {showLoadingOverlay && <LoadingOverlay startTime={processingStartTime} isProcessing={isProcessing} />}
            
            {/* Multiple PDF previews take priority */}
            {fileType === 'pdf' && previewFiles.length > 0 ? (
                <div className="grid grid-cols-3 gap-4">
                    {previewFiles.map((pf, idx) => {
                        const isCurrentFile = file === pf.file; // Determine if this is the current file
                        return (
                            <div key={idx} className="flex flex-col items-center"> 
                                <div 
                                    className={`relative w-48 h-56 bg-bg-tertiary rounded-lg overflow-hidden ${
                                        isCurrentFile 
                                            ? 'border-2 border-brand-primary' 
                                            : 'border-2 border-transparent shadow-none outline-none' 
                                    }`}
                                >
                                    <button
                                        onClick={(e) => removePreview(e, pf.file)}
                                        className="absolute top-1.5 left-1.5 z-10 bg-[var(--bg-tertiary)] hover:bg-[var(--bg-secondary)] rounded-full flex items-center justify-center text-[var(--text-primary)] transition-colors duration-150 w-5 h-5"
                                        title="Remove file"
                                    >
                                        <FiX className="w-3 h-3" />
                                    </button>
                                    <iframe
                                        src={`${pf.previewUrl}#view=FitH`}
                                        className="w-full h-full"
                                        style={{ pointerEvents: 'none', border: 'none' }} 
                                        title={`PDF Preview ${idx + 1}`}
                                    />
                                </div>
                                <p className="text-sm font-medium truncate mt-2">{pf.file.name}</p>
                                <p className="text-xs text-text-secondary">
                                    {(pf.file.size / (1024 * 1024)).toFixed(2)} MB
                                </p>
                            </div>
                        );
                    })}
                </div>
            ) : file ? (
                <>
                    {renderFilePreview()}
                    <p className="text-sm text-text-muted">
                        {t('converter.dropzone.clickToReplace')}
                    </p>
                </>
            ) : (
                <div className="relative z-10 text-center">
                    {/* Animated background decoration */}
                    <div className="absolute inset-0 overflow-hidden pointer-events-none -z-10">
                        <div className="absolute top-10 left-10 w-32 h-32 bg-[var(--brand-primary)]/5 rounded-full blur-2xl animate-pulse"></div>
                        <div className="absolute bottom-10 right-10 w-24 h-24 bg-[var(--brand-primary)]/5 rounded-full blur-2xl animate-pulse" style={{animationDelay: '1s'}}></div>
                    </div>
                    
                    {/* Enhanced upload icon with animation */}
                    <div className="mb-8 relative">
                        <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-r from-[var(--brand-primary)]/20 to-[var(--brand-primary)]/10 rounded-full mb-4 transition-transform duration-300 group-hover:scale-110">
                            <Upload className={`w-12 h-12 transition-colors duration-300 ${isDragActive ? 'text-[var(--brand-primary)]' : 'text-[var(--brand-primary)]/70 group-hover:text-[var(--brand-primary)]'}`} />
                        </div>
                    </div>
                    
                    <div className="space-y-4">
                        <h3 className="text-2xl font-bold text-[var(--text-primary)] mb-2">
                            {isDragActive ? t('converter.dropzone.dragActive') : t('converter.dropzone.title')}
                        </h3>
                        <p className="text-lg text-[var(--text-secondary)] mb-6 max-w-md mx-auto">
                            {t('converter.dropzone.browse')}
                        </p>
                        
                        {/* Enhanced file format info */}
                        <div className="bg-gradient-to-r from-[var(--bg-secondary)] to-[var(--bg-secondary)]/80 border border-[var(--border-primary)] rounded-xl px-6 py-4 inline-block backdrop-blur-sm">
                            <div className="flex items-center gap-3">
                                <FileText className="w-5 h-5 text-[var(--brand-primary)]" />
                                <span className="text-sm font-medium text-[var(--text-primary)]">
                                    {t(`upload.dropzone.supportedFormats${fileType.toUpperCase()}`)}
                                </span>
                            </div>
                            <p className="text-xs text-[var(--text-secondary)] mt-2">
                                Max 10MB
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UploadSection;